/**
 * 视频生成配置节点（卡片极简 + 下方面板架构，对齐 infinite-canvas 布局）：
 *  - 卡片内：模式分段、模型选择、参数摘要按钮（弹层编辑时长/清晰度/比例）、生成按钮
 *  - 下方面板（选中时展开）：上游输入排序、补充提示词、运行链路
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Wand2, Loader2, BookOpen, ArrowUp, ArrowDown, Play, ChevronDown, AtSign, Maximize2, MousePointerClick } from 'lucide-react';
import type { CanvasNode, GenerateVideoNodeData, TextNodeData } from '../types';
import { NodeWrapper } from './NodeWrapper';
import { NodeParamsPopover } from './NodeParamsPopover';
import { boardActions, getBoardState, useBoard } from '../state/boardStore';
import {
  aggregateUpstream,
  aggregateUpstreamVideoRefs,
  aggregateUpstreamAudioReferences,
  buildUpstreamSignature,
  collectUpstreamImageAssetGroups,
  waitForUpstreamIdle,
} from '../state/upstream';
import { resolveAtReferences } from '../atReference';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';
import { runVideoGeneration } from '../generators/videoGenerator';
import { executeChain } from '../chainExecutor';
import { htmlToPlainText } from '../utils/richTextBox';
import { videoApi } from '../../../baselines/video/services/videoApi';
import type { VideoMode, VideoModelPublicInfo } from '../../../baselines/video/services/videoApi';
import { getCachedVideoModels } from '../modelCache';
import { writeNodeParamMemory } from '../utils/nodeParamMemory';
import { PromptLibraryModal } from '../PromptLibraryModal';
import {
  PromptMentionEditor,
  type PromptMentionEditorHandle,
} from './PromptMentionEditor';
import toast from '../../../components/Toast';
import { VideoCountSelector } from '../../../baselines/video/components/VideoCountSelector';
import { TextEditorDialog } from '../components/dialogs/TextEditorDialog';
import { VideoDurationSelector } from '../../../baselines/video/components/VideoDurationSelector';
import { VideoCreditsPreview } from '../../../baselines/video/components/VideoCreditsPreview';
import { VideoAudioToggle } from '../../../baselines/video/components/VideoAudioToggle';
import {
  getAvailableModes,
  getDurationValuesFromSpec,
  getModelCapabilitiesForMode,
  isDurationInSpec,
} from '../../../baselines/video/hooks/useVideoModelCapabilities';
import { useUpstreamModeSync } from '../hooks/useUpstreamModeSync';
import { validateVideoReferenceQuota } from '../utils/referenceImageQuota';

interface GenerateVideoNodeProps {
  node: CanvasNode<GenerateVideoNodeData>;
  selected: boolean;
  highlighted: boolean;
  userCredits?: number;
}

const FALLBACK_ASPECT_OPTIONS = ['16:9', '9:16', '1:1', '4:3', '3:4'];
const FALLBACK_RESOLUTION_OPTIONS = ['480p', '720p', '1080p'];
/** 取不到系统配置时的生成数量兜底上限（与后台默认值一致） */
const FALLBACK_MAX_JOBS_PER_SUBMIT = 4;
const MODE_OPTIONS: Array<{ value: VideoMode; label: string }> = [
  { value: 'text2video', label: '文生视频' },
  { value: 'img2video', label: '图生视频' },
  { value: 'first_last_frame', label: '首尾帧' },
];

export function GenerateVideoNode({ node, selected, highlighted, userCredits }: GenerateVideoNodeProps) {
  const [models, setModels] = useState<VideoModelPublicInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [promptLibOpen, setPromptLibOpen] = useState(false);
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [paramsOpen, setParamsOpen] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [maxJobsPerSubmit, setMaxJobsPerSubmit] = useState(FALLBACK_MAX_JOBS_PER_SUBMIT);
  const mentionEditorRef = useRef<PromptMentionEditorHandle | null>(null);
  const paramsRef = useRef<HTMLDivElement | null>(null);
  // 只订阅上游签名：上游内容不变时本节点不重渲染，避免 memo 被全量 nodes 订阅击穿
  const upstreamSig = useBoard((s) => buildUpstreamSignature(node.id, s.nodes, s.edges));
  const referencePicker = useBoard((s) => s.referencePicker);
  const readOnly = useBoard((s) => s.readOnly);
  const { nodes, edges } = useMemo(() => getBoardState(), [upstreamSig]);
  const pickingActive = referencePicker?.sourceNodeId === node.id;
  const data = node.data;
  const currentMode = data.mode || 'text2video';
  const currentModel = useMemo(
    () => models.find((model) => model.modelId === data.modelId),
    [models, data.modelId]
  );
  const availableModes = useMemo(() => getAvailableModes(currentModel), [currentModel]);
  // 上游聚合（一次计算复用）：模式同步看「有无图」，配额校验看「有几张」
  const upstreamAggregate = useMemo(
    () => aggregateUpstream(node, nodes, edges),
    [node, nodes, edges]
  );
  // 连线驱动的模式同步：上游有无图片输入翻转时在文生视频/图生视频间自动切换
  // （模型未加载时 availableModes 为空，乐观放行，后续模型能力修正逻辑兜底）
  const hasImageUpstream = upstreamAggregate.referenceImages.length > 0;
  useUpstreamModeSync({
    nodeId: node.id,
    hasImageUpstream,
    textMode: 'text2video',
    imgMode: 'img2video',
    imageDependentModes: ['img2video', 'first_last_frame'],
    imgModeAvailable: availableModes.length === 0 || availableModes.includes('img2video'),
    textModeAvailable: availableModes.length === 0 || availableModes.includes('text2video'),
  });
  // 连图强制图生视频：已连图片时 text2video 不使用参考图（提交会静默忽略图片），
  // 纠正为 img2video（含旧项目打开时的存量状态；模式选择器同步禁用文生视频项）
  const img2videoAvailable = availableModes.length === 0 || availableModes.includes('img2video');
  useEffect(() => {
    if (readOnly) return;
    if (!hasImageUpstream || currentMode !== 'text2video' || !img2videoAvailable) return;
    boardActions.updateNodeDataSilent(node.id, { mode: 'img2video' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasImageUpstream, currentMode, img2videoAvailable, node.id, readOnly]);
  const currentCaps = useMemo(
    () => getModelCapabilitiesForMode(currentModel, currentMode),
    [currentModel, currentMode]
  );
  // 生成数量上限与视频工作台同源（公开系统配置，仅需登录）；拿不到时按后台默认值兜底
  useEffect(() => {
    let cancelled = false;
    videoApi
      .getPublicSystemConfig()
      .then((conf) => {
        if (cancelled) return;
        const max = Number(conf.maxJobsPerSubmit);
        if (Number.isFinite(max) && max >= 1) setMaxJobsPerSubmit(Math.floor(max));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const count = Math.max(1, Math.min(data.count ?? 1, maxJobsPerSubmit));
  // 存量数据 / 参数记忆里的数量可能超出当前配置，静默收敛到合法区间
  useEffect(() => {
    if (readOnly) return;
    const desired = Math.max(1, Math.min(data.count ?? 1, maxJobsPerSubmit));
    if (desired !== data.count) {
      boardActions.updateNodeDataSilent(node.id, { count: desired });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxJobsPerSubmit, data.count, node.id, readOnly]);
  // 上游参考视频（画布视频消费的唯一例外）：仅模型+模式支持时随任务提交。
  // 不支持但已连接（存量边/先连后换模型）时仅提示忽略，不参与提交。
  const upstreamVideoRefs = useMemo(
    () => aggregateUpstreamVideoRefs(node.id, nodes, edges),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [upstreamSig, node.id]
  );
  const upstreamVideoUrls = upstreamVideoRefs.map((ref) => ref.url);
  const upstreamVideoDurationSec = upstreamVideoRefs.reduce((sum, ref) => sum + ref.durationSec, 0);
  const videoReferenceCapable = currentCaps.enableVideoReference;
  // 上游参考音频（声音节点连线聚合）：仅模型+模式支持时随任务提交。
  // 不支持但已连接（存量边/先连后换模型）时仅提示忽略，不参与提交。
  const upstreamAudioUrls = useMemo(
    () => aggregateUpstreamAudioReferences(node.id, nodes, edges),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [upstreamSig, node.id]
  );
  const audioReferenceCapable = currentCaps.enableAudioReference;
  // @引用预览：聚合提示词含 @图N 标记时，参考图按标记实际引用数计
  // （与 runVideoGeneration 提交口径一致，避免"@只引 1 张但按全量配额误置灰"）
  const upstreamAssetGroups = useMemo(
    () => collectUpstreamImageAssetGroups(node.id, nodes, edges),
    [node.id, nodes, edges]
  );
  const knownRefNos = useMemo(
    () =>
      new Set(
        nodes
          .filter((n) => n.type === 'image' || n.type === 'image_group')
          .map((n) => (n.data as Record<string, unknown>).refNo)
          .filter((v): v is number => typeof v === 'number')
      ),
    [nodes]
  );
  const atPreview = useMemo(
    () => resolveAtReferences(upstreamAggregate.prompt, upstreamAssetGroups, knownRefNos),
    [upstreamAggregate, upstreamAssetGroups, knownRefNos]
  );
  const effectiveReferenceCount = atPreview
    ? atPreview.referenceImages.length
    : upstreamAggregate.referenceImages.length;
  // 参考图数量配额：超出当前模式能力（上限 / 离散集合 / 首尾帧恰好 2 张）时置灰生成入口
  const quotaError = useMemo(
    () =>
      validateVideoReferenceQuota(
        currentMode,
        effectiveReferenceCount,
        currentCaps
      ),
    [currentMode, effectiveReferenceCount, currentCaps]
  );
  const durationSpec = currentCaps.available
    ? currentCaps.supportedDurations
    : { type: 'enum' as const, values: [data.duration ?? currentModel?.defaults.duration ?? 5] };
  const resolutionOptions =
    currentCaps.available && currentCaps.supportedResolutions.length > 0
      ? currentCaps.supportedResolutions
      : FALLBACK_RESOLUTION_OPTIONS;
  const aspectOptions =
    currentCaps.available && currentCaps.supportedAspectRatios.length > 0
      ? currentCaps.supportedAspectRatios
      : FALLBACK_ASPECT_OPTIONS;

  useEffect(() => {
    let cancelled = false;
    getCachedVideoModels()
      .then((res) => {
        if (cancelled) return;
        setModels(res);
        if (readOnly) return;
        const current = res.find((model) => model.modelId === node.data.modelId);
        const fallback = current ?? res[0];
        if (!fallback) return;
        if (!current) {
          // 默认模型回填属于派生校正，静默写入不触发自动保存
          boardActions.updateNodeDataSilent(node.id, {
            modelId: fallback.modelId,
            duration: node.data.duration ?? fallback.defaults.duration,
            resolution: node.data.resolution ?? fallback.defaults.resolution,
            aspectRatio: node.data.aspectRatio ?? fallback.defaults.aspectRatio,
          });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (readOnly) return;
    if (!currentModel) return;

    const patch: Partial<GenerateVideoNodeData> = {};
    const resolvedMode = availableModes.includes(currentMode) ? currentMode : availableModes[0];

    if (!resolvedMode) return;
    if (resolvedMode !== currentMode) {
      patch.mode = resolvedMode;
    }

    const caps = getModelCapabilitiesForMode(currentModel, resolvedMode);
    if (!caps.available) return;

    const durationValues = getDurationValuesFromSpec(caps.supportedDurations);
    const preferredDuration = isDurationInSpec(caps.supportedDurations, currentModel.defaults.duration)
      ? currentModel.defaults.duration
      : durationValues[0];
    const currentDuration = data.duration ?? preferredDuration ?? 5;
    if (!isDurationInSpec(caps.supportedDurations, currentDuration)) {
      patch.duration = preferredDuration ?? currentDuration;
    }

    if (caps.supportedResolutions.length > 0) {
      const preferredResolution = caps.supportedResolutions.includes(currentModel.defaults.resolution)
        ? currentModel.defaults.resolution
        : caps.supportedResolutions[0];
      if (!data.resolution || !caps.supportedResolutions.includes(data.resolution)) {
        patch.resolution = preferredResolution;
      }
    }

    if (caps.supportedAspectRatios.length > 0) {
      const preferredAspectRatio = caps.supportedAspectRatios.includes(currentModel.defaults.aspectRatio)
        ? currentModel.defaults.aspectRatio
        : caps.supportedAspectRatios[0];
      if (!data.aspectRatio || !caps.supportedAspectRatios.includes(data.aspectRatio)) {
        patch.aspectRatio = preferredAspectRatio;
      }
    }

    if (Object.keys(patch).length > 0) {
      // 按模型能力收敛参数属于派生校正，静默写入
      boardActions.updateNodeDataSilent(node.id, patch as Record<string, unknown>);
    }
  }, [
    availableModes,
    currentMode,
    currentModel,
    data.aspectRatio,
    data.duration,
    data.resolution,
    node.id,
  ]);

  const onChange = (patch: Partial<GenerateVideoNodeData>) => {
    boardActions.updateNodeData(node.id, patch as Record<string, unknown>);
    // 记住用户的参数偏好（模型 / 时长 / 清晰度 / 比例 / 运镜 / 音轨），下次新建生视频节点自动套用
    writeNodeParamMemory('generate_video', patch);
  };

  const handleGenerate = async () => {
    if (loading) return;
    setLoading(true);
    try {
      // 上游链路执行中：等待完成后再聚合（防止聚合到流式中途等不完整输出），聚合前重读最新图状态
      await waitForUpstreamIdle(node.id);
      const fresh = getBoardState();
      const upstream = aggregateUpstream(node, fresh.nodes, fresh.edges);
      if (!upstream.prompt) {
        toast.error('请先连接上游文本节点或在本节点填写提示词');
        return;
      }
      await runVideoGeneration({
        generateNode: node,
        prompt: upstream.prompt,
        referenceImages: upstream.referenceImages,
        firstFrameImage: upstream.firstFrameImage,
        lastFrameImage: upstream.lastFrameImage,
        ...(videoReferenceCapable && upstreamVideoUrls.length > 0
          ? {
              referenceVideoUrls: upstreamVideoUrls,
              referenceVideoDurations: upstreamVideoRefs.map((ref) => ref.durationSec),
            }
          : {}),
        ...(audioReferenceCapable && upstreamAudioUrls.length > 0
          ? { audioReferenceUrls: upstreamAudioUrls }
          : {}),
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '生成失败');
    } finally {
      setLoading(false);
    }
  };

  const handleRunChain = async () => {
    try {
      await executeChain(node.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '链路执行失败');
    }
  };

  const generating = !!data.generating || loading;
  const modeLabel = MODE_OPTIONS.find((m) => m.value === currentMode)?.label ?? '文生视频';

  // ---------------- 下方面板 ----------------
  const upstreamPanel = (() => {
    const upstreamIds = edges
      .filter((e) => e.targetNodeId === node.id)
      .map((e) => e.sourceNodeId);
    const upstreamNodes = nodes.filter((n) => upstreamIds.includes(n.id));
    if (upstreamNodes.length === 0) return null;
    const inputOrder = (data as Record<string, unknown>).inputOrder as string[] | undefined;
    const ordered = inputOrder && inputOrder.length > 0
      ? [...upstreamNodes].sort((a, b) => {
          const oa = inputOrder.indexOf(a.id);
          const ob = inputOrder.indexOf(b.id);
          return (oa === -1 ? 9999 : oa) - (ob === -1 ? 9999 : ob);
        })
      : upstreamNodes;
    const moveItem = (id: string, dir: -1 | 1) => {
      const currentOrder = (data as Record<string, unknown>).inputOrder as string[] | undefined
        || upstreamIds;
      const idx = currentOrder.indexOf(id);
      if (idx === -1) return;
      const newOrder = [...currentOrder];
      const swapIdx = idx + dir;
      if (swapIdx < 0 || swapIdx >= newOrder.length) return;
      [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
      onChange({ inputOrder: newOrder } as Record<string, unknown> as Partial<GenerateVideoNodeData>);
    };
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
        <div className="mb-1 text-[10px] font-medium text-slate-400">
          上游输入 ({upstreamNodes.length})，首尾帧模式按顺序取前两张；@图N 按图号引用上游图片（见图上角标），不受连线顺序影响
        </div>
        {ordered.map((n, i) => (
          <div key={n.id} className="flex items-center gap-1.5 py-0.5">
            <span className="text-[10px] text-slate-400">{i + 1}.</span>
            <span className="flex-1 truncate text-slate-600">
              {n.type === 'text'
                 ? htmlToPlainText((n.data as TextNodeData).content || '').slice(0, 30) || '(空文本)'
                : n.type === 'image'
                  ? '图片'
                  : n.type === 'image_group'
                    ? '图片组'
                    : n.type === 'video'
                      ? '视频'
                      : n.type === 'audio'
                        ? '声音'
                        : n.type === 'llm'
                          ? 'LLM'
                          : n.type}
            </span>
            <button
              type="button"
              onClick={() => moveItem(n.id, -1)}
              className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600 disabled:opacity-30"
              disabled={i === 0}
            >
              <ArrowUp className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => moveItem(n.id, 1)}
              className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600 disabled:opacity-30"
              disabled={i === ordered.length - 1}
            >
              <ArrowDown className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    );
  })();

  const panel = (
    <div className="space-y-2">
      {upstreamPanel}
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-[10px] font-medium text-slate-400">补充提示词（与上游文本合并）</label>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() =>
                pickingActive
                  ? boardActions.cancelReferencePick()
                  : boardActions.startReferencePick(node.id)
              }
              className={`flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] transition ${
                pickingActive ? 'bg-blue-100 text-blue-700' : 'text-blue-500 hover:bg-blue-50'
              }`}
              title="进入点选模式，在画布上点击节点以连接为参考输入"
            >
              <MousePointerClick className="h-3 w-3" />
              {pickingActive ? '退出点选' : '点选'}
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={() => setMentionOpen((v) => !v)}
                disabled={upstreamAssetGroups.length === 0}
                className="flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                title="插入 @图N 引用：提示词中显式指定某张上游图片作参考图"
              >
                <AtSign className="h-3 w-3" />
                引用
              </button>
              {mentionOpen && (() => {
                // 仅列出有图上游；@图N 按节点稳定图号（角标 #N）插入，与连线顺序无关
                if (upstreamAssetGroups.length === 0) {
                  return (
                    <div className="absolute right-0 top-full z-30 mt-1 w-[170px] rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[10px] text-slate-400 shadow-lg">
                      上游还没有可引用的图片，先连接图片节点或图片组
                    </div>
                  );
                }
                return (
                  <div className="absolute right-0 top-full z-30 mt-1 max-h-44 min-w-[170px] overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                    {upstreamAssetGroups.map((group) => {
                      const refNo = group.refNo;
                      if (typeof refNo !== 'number') return null;
                      const src = resolveBrowserMediaUrl(group.urls[0]) || group.urls[0];
                      return (
                        <button
                          key={group.nodeId}
                          type="button"
                          onClick={() => {
                            mentionEditorRef.current?.insertMention(refNo);
                            setMentionOpen(false);
                          }}
                          className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs text-slate-600 hover:bg-slate-50"
                          title={
                            group.urls.length > 1
                              ? `插入 @图${refNo}（该上游为图片组，共 ${group.urls.length} 张）`
                              : `插入 @图${refNo}`
                          }
                        >
                          <img src={src} alt="" className="h-6 w-6 rounded border border-slate-100 object-cover" />
                          <span className="truncate">
                            @图{refNo}
                            {group.type === 'image_group' && (
                              <span className="ml-1 text-[10px] text-slate-400">（组 {group.urls.length} 张）</span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            <button
              type="button"
              onClick={() => setPromptLibOpen(true)}
              className="flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] text-blue-500 hover:bg-blue-50"
              title="从提示词库选择"
            >
              <BookOpen className="h-3 w-3" />
              提示词库
            </button>
            <button
              type="button"
              onClick={() => setPromptExpanded(true)}
              className="flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] text-slate-500 hover:bg-slate-100"
              title="放大编辑"
            >
              <Maximize2 className="h-3 w-3" />
            </button>
          </div>
        </div>
        <div className="relative">
          <PromptMentionEditor
            ref={mentionEditorRef}
            value={data.prompt || ''}
            onChange={(v) => onChange({ prompt: v })}
            placeholder={'补充提示词，与上游文本合并；可用 @图N（见图上角标）引用某张上游图片作参考图'}
            className="h-16 resize-none"
            options={upstreamAssetGroups
              .filter((g): g is typeof g & { refNo: number } => typeof g.refNo === 'number')
              .map((g) => ({
                refNo: g.refNo,
                thumbnail: resolveBrowserMediaUrl(g.urls[0]) || g.urls[0],
                isGroup: g.type === 'image_group',
                count: g.urls.length,
              }))}
          />
        </div>
      </div>
      <button
        type="button"
        onClick={handleRunChain}
        disabled={generating || !!quotaError}
        title={quotaError ?? undefined}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-fuchsia-100 py-1.5 text-xs font-medium text-fuchsia-700 transition hover:bg-fuchsia-200 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <Play className="h-3.5 w-3.5" />
        <span>运行链路</span>
      </button>
    </div>
  );

  // ---------------- 卡片 ----------------
  return (
    <NodeWrapper node={node} selected={selected} highlighted={highlighted} panel={panel} stretchContent>
      <div className="flex min-h-0 flex-1 flex-col gap-1.5 text-[11px]">
        {/* 模式分段 */}
        <div className="flex rounded-lg bg-slate-100 p-0.5">
          {MODE_OPTIONS.map((opt) => {
            const modeUnavailable = !!currentModel && !availableModes.includes(opt.value);
            // 已连参考图时文生视频不消费图片（提交会静默忽略），禁用并强制图生视频
            const blockedByImages =
              opt.value === 'text2video' && hasImageUpstream && img2videoAvailable;
            const disabled = modeUnavailable || blockedByImages;
            return (
              <button
                key={opt.value}
                type="button"
                disabled={disabled}
                onClick={() => onChange({ mode: opt.value })}
                title={blockedByImages ? '已连接参考图，已切换为图生视频' : undefined}
                className={`flex-1 rounded-md py-1 text-[10px] font-medium transition ${
                  currentMode === opt.value
                    ? 'bg-white text-slate-800 shadow-sm'
                    : disabled
                      ? 'text-slate-300'
                      : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* 模型 + 参数摘要（弹层） */}
        <div className="flex gap-1.5">
          <select
            value={data.modelId || ''}
            onChange={(e) => onChange({ modelId: e.target.value })}
            className="min-w-0 flex-1 rounded-md border border-light-border bg-white px-2 py-1.5 text-xs"
            title="视频模型"
          >
            <option value="">选择视频模型</option>
            {models.map((m) => (
              <option key={m.modelId} value={m.modelId}>{m.name}</option>
            ))}
          </select>
          <div className="relative" ref={paramsRef}>
            <button
              type="button"
              onClick={() => setParamsOpen((v) => !v)}
              className={`flex h-full items-center gap-1 rounded-md border px-2 text-[10px] font-medium transition ${
                paramsOpen ? 'border-purple-300 bg-purple-50 text-purple-700' : 'border-light-border bg-white text-slate-600 hover:bg-slate-50'
              }`}
              title="时长 · 清晰度 · 比例"
            >
              {(data.duration ?? currentModel?.defaults.duration ?? 5)}s · {data.resolution || currentModel?.defaults.resolution || '720p'} · {data.aspectRatio || currentModel?.defaults.aspectRatio || aspectOptions[0] || '16:9'}
              {count > 1 ? ` × ${count}` : ''}
              <ChevronDown className="h-3 w-3" />
            </button>
            <NodeParamsPopover open={paramsOpen} onClose={() => setParamsOpen(false)} anchor={paramsRef} width={224}>
              <VideoDurationSelector
                value={data.duration ?? currentModel?.defaults.duration ?? 5}
                onChange={(duration) => onChange({ duration })}
                spec={durationSpec}
                compact
              />
              <div>
                <label className="mb-0.5 block text-[10px] text-slate-400">清晰度</label>
                <select
                  value={data.resolution || currentModel?.defaults.resolution || resolutionOptions[0] || '720p'}
                  onChange={(e) => onChange({ resolution: e.target.value })}
                  className="w-full rounded-md border border-light-border bg-white px-2 py-1 text-xs"
                >
                  {resolutionOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-0.5 block text-[10px] text-slate-400">比例</label>
                <select
                  value={data.aspectRatio || currentModel?.defaults.aspectRatio || aspectOptions[0] || '16:9'}
                  onChange={(e) => onChange({ aspectRatio: e.target.value })}
                  className="w-full rounded-md border border-light-border bg-white px-2 py-1 text-xs"
                >
                  {aspectOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <VideoCountSelector
                value={count}
                onChange={(next) => onChange({ count: next })}
                max={maxJobsPerSubmit}
                compact
              />
              {currentCaps.enableAudio && (
                <div className="pt-1">
                  <VideoAudioToggle
                    value={Boolean(data.enableAudio)}
                    onChange={(v) => onChange({ enableAudio: v })}
                  />
                </div>
              )}
            </NodeParamsPopover>
          </div>
        </div>

        <VideoCreditsPreview
          modelId={data.modelId || ''}
          mode={currentMode}
          duration={data.duration ?? currentModel?.defaults.duration ?? 5}
          resolution={data.resolution || currentModel?.defaults.resolution || resolutionOptions[0] || '720p'}
          n={count}
          enableAudio={currentCaps.enableAudio ? Boolean(data.enableAudio) : false}
          refVideoDurationSec={
            videoReferenceCapable ? upstreamVideoDurationSec : 0
          }
          refImageCount={
            currentMode === 'img2video'
              ? effectiveReferenceCount
              : currentMode === 'first_last_frame' && effectiveReferenceCount === 2
                ? 2
                : 0
          }
          userCredits={userCredits}
        />

        {/* 参考图数量配额不满足：置灰生成入口并说明差距 */}
        {quotaError && (
          <p className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[10px] leading-relaxed text-red-600">
            {quotaError}
          </p>
        )}

        {/* 上游参考视频提示：支持时随任务提交；不支持（存量边/换模型）时明示忽略 */}
        {upstreamVideoUrls.length > 0 && (
          <p
            className={`rounded-md border px-2 py-1 text-[10px] leading-relaxed ${
              videoReferenceCapable
                ? 'border-purple-200 bg-purple-50 text-purple-600'
                : 'border-amber-200 bg-amber-50 text-amber-600'
            }`}
          >
            已连接 {upstreamVideoUrls.length} 个参考视频
            {videoReferenceCapable ? '，将随任务提交' : '，当前模型/模式不支持参考视频，提交时将忽略'}
          </p>
        )}

        {/* 上游参考音频提示：支持时随任务提交；不支持（存量边/换模型）时明示忽略 */}
        {upstreamAudioUrls.length > 0 && (
          <p
            className={`rounded-md border px-2 py-1 text-[10px] leading-relaxed ${
              audioReferenceCapable
                ? 'border-pink-200 bg-pink-50 text-pink-600'
                : 'border-amber-200 bg-amber-50 text-amber-600'
            }`}
          >
            已连接 {upstreamAudioUrls.length} 个参考音频
            {audioReferenceCapable ? '，将随任务提交' : '，当前模型/模式不支持参考音频，提交时将忽略'}
          </p>
        )}

        <button
          type="button"
          onClick={handleGenerate}
          disabled={loading || !!quotaError}
          title={quotaError ?? undefined}
          className="mt-auto flex w-full items-center justify-center gap-2 rounded-lg bg-purple-500 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-purple-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {generating ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>重新生成</span>
            </>
          ) : (
            <>
              <Wand2 className="h-3.5 w-3.5" />
              <span>生成视频</span>
            </>
          )}
        </button>
        <p className="text-center text-[9px] text-slate-300">{modeLabel}</p>
      </div>

      <PromptLibraryModal
        open={promptLibOpen}
        onClose={() => setPromptLibOpen(false)}
        onSelect={(prompt) => {
          const existing = data.prompt || '';
          onChange({ prompt: existing ? `${existing}\n${prompt}` : prompt });
        }}
      />
      {promptExpanded && (
        <TextEditorDialog
          title="补充提示词"
          initialValue={data.prompt || ''}
          placeholder="补充提示词，与上游文本合并；可用 @图N（见图上角标）引用某张上游图片作参考图"
          onCommit={(html) => {
            onChange({ prompt: htmlToPlainText(html) });
            setPromptExpanded(false);
          }}
          onClose={() => setPromptExpanded(false)}
        />
      )}
    </NodeWrapper>
  );
}
